// Simple async-fetch hook: tracks (data, loading, error) and re-runs
// when the key changes or `refresh()` is called. Keeps the
// component-level data layer dep-free (no React Query for MVP).

import { useCallback, useEffect, useState } from "react";

import { ApiError } from "../lib/api";

export interface AsyncState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  refresh: () => void;
}

export function useAsync<T>(
  factory: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoFactory = useCallback(factory, deps);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    memoFactory()
      .then((value) => {
        if (!cancelled) {
          setData(value);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoading(false);
        if (err instanceof ApiError) setError(err);
        else setError(new ApiError(0, "UNKNOWN", String(err)));
      });
    return () => {
      cancelled = true;
    };
  }, [memoFactory, tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  return { data, error, loading, refresh };
}
